/**
 * InstanceQuickEditDialog — opens the same ModuleConfigFields editor the
 * Module Instances tab uses, but scoped to a single instance. Callers
 * (e.g. the Page Builder) pass an `instanceId`; the dialog fetches the
 * instance on open, lets the admin tweak `name` + `config`, then saves
 * with a single PUT and calls `onSaved` so the caller can refresh its
 * own state.
 *
 * Kept intentionally simple — no autosave, no reset, no clone. That
 * lives on the dedicated Module Instances screen. This dialog is a
 * "make a quick tweak without leaving the page builder" affordance.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check, CircleAlert } from "lucide-react";
import ModuleConfigFields from "./ModuleConfigFields";
import SectionBackgroundControls from "./SectionBackgroundControls";
import CustomScriptControls from "./CustomScriptControls";

const API = getApiUrl();

// Reusable autosave status pill — mirrors the one in the Module Instances
// tab so admins get identical feedback across both editors.
function AutosaveBadge({ state }) {
  if (state === "saving") {
    return (
      <span className="text-xs flex items-center gap-1 text-muted-foreground" data-testid="quick-edit-autosave-saving">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="text-xs flex items-center gap-1 text-green-600" data-testid="quick-edit-autosave-saved">
        <Check className="h-3 w-3" /> Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="text-xs flex items-center gap-1 text-red-600" data-testid="quick-edit-autosave-error">
        <CircleAlert className="h-3 w-3" /> Save failed
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">Changes auto-save</span>;
}

export default function InstanceQuickEditDialog({
  open,
  onOpenChange,
  instanceId,
  onSaved,
  allPages,
}) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [instance, setInstance] = useState(null);
  const [name, setName] = useState("");
  const [configFields, setConfigFields] = useState({});
  const saving = false; // legacy flag kept so the JSX below can gate the button; autosave handles the real writes.
  // Debounced autosave — mirrors the Module Instances tab. See
  // `loadedSnapshotRef` below for how the first-render no-op save is
  // avoided (diffed against what GET actually returned, not a timer).
  const [autosaveState, setAutosaveState] = useState("idle");
  const autosaveTimer = useRef(null);
  // Snapshot of exactly what's in the DB right after GET — the autosave
  // effect below diffs the live name/config against this, so nothing is
  // written until something has genuinely changed from what was loaded
  // (no more "first render fires a save" races with a time-based flag).
  const loadedSnapshotRef = useRef(null);
  // Stash the callback props in refs so their (inline arrow) identity on
  // every parent render never re-triggers the fetch or autosave effects.
  // Without this the dialog re-mounts itself in a loop → "flashing" UI.
  const onOpenChangeRef = useRef(onOpenChange);
  const onSavedRef = useRef(onSaved);
  useEffect(() => { onOpenChangeRef.current = onOpenChange; }, [onOpenChange]);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);

  // Fetch instance whenever the dialog opens for a new instance id.
  useEffect(() => {
    if (!open || !instanceId) return;
    let alive = true;
    setLoading(true);
    setAutosaveState("idle");
    axios
      .get(`${API}/admin/module-instances/${instanceId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        // Endpoint returns the instance doc directly, not wrapped.
        const inst = data?.instance || data;
        setInstance(inst || null);
        setName(inst?.name || "");
        setConfigFields(inst?.config || {});
        loadedSnapshotRef.current = JSON.stringify({
          name: (inst?.name || "").trim(),
          config: inst?.config || {},
        });
      })
      .catch((e) => {
        if (!alive) return;
        toast.error(e.response?.data?.detail || "Failed to load instance");
        onOpenChangeRef.current?.(false);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, instanceId]);

  // Debounced autosave — fires 800ms after the last edit while the dialog
  // is open, but only once the live name/config actually differs from
  // what was loaded (see loadedSnapshotRef above).
  useEffect(() => {
    if (!open || !instance) return;
    const currentSnapshot = JSON.stringify({ name: name.trim(), config: configFields });
    if (currentSnapshot === loadedSnapshotRef.current) {
      setAutosaveState("idle");
      return;
    }
    setAutosaveState("dirty");
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      if (!name.trim()) {
        setAutosaveState("error");
        return;
      }
      try {
        setAutosaveState("saving");
        await axios.put(
          `${API}/admin/module-instances/${instanceId}`,
          { name: name.trim(), config: configFields },
          { withCredentials: true },
        );
        loadedSnapshotRef.current = currentSnapshot;
        setAutosaveState("saved");
        onSavedRef.current?.();
      } catch (e) {
        setAutosaveState("error");
        toast.error(e.response?.data?.detail || "Auto-save failed");
      }
    }, 800);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [name, configFields, open, instance, instanceId]);

  // Flush any pending autosave before closing so the last-typed change
  // isn't lost when the admin clicks Cancel or dismisses.
  const flushAutoSave = async () => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    if (!instance || !name.trim()) return;
    const currentSnapshot = JSON.stringify({ name: name.trim(), config: configFields });
    if (currentSnapshot === loadedSnapshotRef.current) return;
    try {
      await axios.put(
        `${API}/admin/module-instances/${instanceId}`,
        { name: name.trim(), config: configFields },
        { withCredentials: true },
      );
      loadedSnapshotRef.current = currentSnapshot;
      onSavedRef.current?.();
    } catch {
      /* toast already surfaced by the debounced path */
    }
  };

  const handleImageUpload = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url || data.path;
      if (url) setConfigFields((prev) => ({ ...prev, [field]: url }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={async (v) => {
      if (saving) return;
      if (!v) await flushAutoSave();
      onOpenChangeRef.current?.(v);
    }}>
      <DialogContent
        className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto"
        data-testid="instance-quick-edit-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Edit module instance</DialogTitle>
              <DialogDescription>
                Changes apply everywhere this instance is used across the site.
              </DialogDescription>
            </div>
            <div className="pt-1"><AutosaveBadge state={autosaveState} /></div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : instance ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Instance name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hero — Homepage"
                data-testid="quick-edit-instance-name"
              />
              <p className="text-xs text-muted-foreground">
                Type: <span className="font-medium">{instance.module_type}</span>
              </p>
            </div>

            <div className="pt-2 border-t space-y-4">
              <ModuleConfigFields
                moduleType={instance.module_type}
                configFields={configFields}
                setConfigFields={setConfigFields}
                uploading={uploading}
                onImageUpload={handleImageUpload}
                allPages={allPages}
                instanceId={instance.instance_id}
              />

              {/* Universal per-instance spacing — mirror of ModuleInstances */}
              <div className="pt-4 border-t">
                <div className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide">Spacing</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Top gap (px)</label>
                    <input
                      type="number"
                      min="0"
                      max="256"
                      step="4"
                      value={configFields.top_gap ?? 10}
                      onChange={(e) => setConfigFields((prev) => ({ ...prev, top_gap: Number(e.target.value) || 0 }))}
                      className="w-full h-9 px-3 border border-input rounded text-sm"
                      data-testid="quick-edit-top-gap"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Bottom gap (px)</label>
                    <input
                      type="number"
                      min="0"
                      max="256"
                      step="4"
                      value={configFields.bottom_gap ?? 10}
                      onChange={(e) => setConfigFields((prev) => ({ ...prev, bottom_gap: Number(e.target.value) || 0 }))}
                      className="w-full h-9 px-3 border border-input rounded text-sm"
                      data-testid="quick-edit-bottom-gap"
                    />
                  </div>
                </div>
              </div>

              <SectionBackgroundControls
                config={configFields}
                onChange={(patch) => setConfigFields((prev) => ({ ...prev, ...patch }))}
              />

              <CustomScriptControls
                config={configFields}
                onChange={(patch) => setConfigFields((prev) => ({ ...prev, ...patch }))}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Instance not found.
          </p>
        )}

        <DialogFooter>
          <Button
            onClick={async () => { await flushAutoSave(); onOpenChangeRef.current?.(false); }}
            disabled={saving || loading || !instance}
            data-testid="quick-edit-save"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving</>
            ) : (
              "Done"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
